import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "firebase.js";
import { getRegistrationsByTournament } from "services/registrationService";
import ManualLocationRankingLinker from "./ManualLocationRankingLinker";
import * as Sentry from "@sentry/react";

const normalizeName = (name) =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .toUpperCase()
    .trim();

const LocationRankingUpdater = ({
  locationId,
  users = [],
  locationRankings = [],
}) => {
  const [unrankedUsers, setUnrankedUsers] = useState([]);
  const [unlinkedRankings, setUnlinkedRankings] = useState([]);

  useEffect(() => {
    const syncRankings = async () => {
      try {
        // 1. Link rankings with missing userId
        const incompleteRankings = locationRankings.filter((r) => !r.userId);
        setUnlinkedRankings(incompleteRankings);

        for (const ranking of incompleteRankings) {
          const matchedUser = users.find(
            (u) => normalizeName(u.name) === normalizeName(ranking.playerName)
          );
          if (matchedUser) {
            const rankingRef = doc(db, "locationRankings", ranking.id);
            await updateDoc(rankingRef, { userId: matchedUser.id });
          }
        }

        // 2. Get all user IDs registered in tournaments at this location
        const tournamentsQuery = query(
          collection(db, "tournaments"),
          where("locationId", "==", locationId)
        );
        const tournamentsSnapshot = await getDocs(tournamentsQuery);
        const tournamentIds = tournamentsSnapshot.docs.map((doc) => doc.id);

        const registeredUserIds = new Set();

        for (const tournamentId of tournamentIds) {
          const regs = await getRegistrationsByTournament(tournamentId);
          regs.forEach((reg) => {
            if (reg.userId) registeredUserIds.add(reg.userId);
            if (reg.teamMateId) registeredUserIds.add(reg.teamMateId);
          });
        }

        // 3. Build ranked + unranked sets
        const updatedRankings =
          incompleteRankings.length > 0
            ? await getDocs(collection(db, "locationRankings")).then((snap) =>
                snap.docs
                  .map((doc) => ({ id: doc.id, ...doc.data() }))
                  .filter((r) => r.locationId === locationId)
              )
            : locationRankings;

        const rankedUserIds = new Set(
          updatedRankings.filter((r) => r.userId).map((r) => r.userId)
        );

        const unranked = users.filter(
          (u) => registeredUserIds.has(u.id) && !rankedUserIds.has(u.id)
        );

        setUnrankedUsers(unranked);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error syncing location rankings:", error);
      }
    };

    syncRankings();
  }, [locationId, users, locationRankings]);

  return (
    <div className="bg-gray-700 text-white p-6 shadow-xl max-w-3xl mx-2 md:mx-auto mt-8">
      <h2 className="text-2xl font-bold mb-4 text-center">
        Utilisateurs non classés pour {locationId}
      </h2>
      {unrankedUsers.length === 0 ? (
        <p className="text-center">
          Tous les joueurs enregistrés sont classés ✅
        </p>
      ) : (
        <ul className="space-y-2">
          {unrankedUsers.map((user) => (
            <ManualLocationRankingLinker
              key={user.id}
              user={user}
              locationId={locationId}
              unlinkedRankings={unlinkedRankings}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default LocationRankingUpdater;
